import { create } from "zustand";
import { createCatalogAssetStoragePath, createTileId } from "../types/catalogIdentifiers.js";
import { resolveTileAssetUrl } from "./tileAssetUrls.js";
const EMPTY_MANIFEST = {
    version: 1,
    defaults: {
        defaultOceanTileSetId: "",
        defaultSecondaryPaintTileSetId: "",
    },
    tileSets: [],
    tiles: [],
};
const EMPTY_BROWSE = {
    creators: [],
    collections: [],
    taxonomy: [],
    rootTileSetEntries: [],
    decorations: [],
    tags: [],
};
function uniqueIds(values) {
    return [...new Set(values.filter(Boolean))];
}
function getManifestTaxonomyNodeIds(rawGroup) {
    if (Array.isArray(rawGroup.taxonomyNodeIds) && rawGroup.taxonomyNodeIds.length > 0) {
        return uniqueIds(rawGroup.taxonomyNodeIds);
    }
    if (typeof rawGroup.category === "string" && rawGroup.category.trim()) {
        return [`official-${rawGroup.category.toLowerCase()}`];
    }
    return [];
}
function normalizePaintMode(rawGroup) {
    if (rawGroup.paintMode === "dynamic" || rawGroup.paintMode === "ordered") {
        return rawGroup.paintMode;
    }
    return rawGroup.tileIds.length > 1 ? "dynamic" : "ordered";
}
function getOrderedBrushEntryKey(entry) {
    return entry.kind === "tile" ? `tile:${entry.tileId}` : `tile-set:${entry.tileSetId}`;
}
function uniqueOrderedBrushEntries(entries) {
    const seen = new Set();
    return entries.filter((entry) => {
        const key = getOrderedBrushEntryKey(entry);
        if (seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}
function normalizeOrderedEntriesForGroup(rawGroup, rawTileSetIndex, tileIndex) {
    if (!Array.isArray(rawGroup.orderedEntries) || rawGroup.orderedEntries.length === 0) {
        return [];
    }
    const ownTileIdSet = new Set(rawGroup.tileIds.filter((tileId) => tileIndex.has(tileId)));
    const entries = [];
    for (const entry of rawGroup.orderedEntries) {
        if (!entry || typeof entry !== "object" || typeof entry.kind !== "string") {
            continue;
        }
        if (entry.kind === "tile") {
            if (typeof entry.tileId === "string" && ownTileIdSet.has(entry.tileId)) {
                entries.push({ kind: "tile", tileId: entry.tileId });
            }
            continue;
        }
        if (entry.kind !== "tile-set" || typeof entry.tileSetId !== "string") {
            continue;
        }
        const childTileSet = rawTileSetIndex.get(entry.tileSetId);
        if (!childTileSet || childTileSet.id === rawGroup.id) {
            continue;
        }
        const childTileIds = childTileSet.tileIds.filter((tileId) => tileIndex.has(tileId));
        if (normalizePaintMode({ paintMode: childTileSet.paintMode, tileIds: childTileIds }) !== "dynamic") {
            continue;
        }
        if (childTileIds.length < 2) {
            continue;
        }
        entries.push({ kind: "tile-set", tileSetId: childTileSet.id });
    }
    return uniqueOrderedBrushEntries(entries);
}
function buildCatalogState(manifest) {
    const tileIndex = new Map();
    const tileByFilenameIndex = new Map();
    const customTileIdSet = new Set();
    const customFilenameSet = new Set();
    const rawTileSetIndex = new Map(manifest.tileSets.map((rawGroup) => [rawGroup.id, rawGroup]));
    for (const rawTile of manifest.tiles) {
        const tile = {
            ...rawTile,
            url: resolveTileAssetUrl(rawTile.relativePath),
        };
        tileIndex.set(tile.id, tile);
        tileByFilenameIndex.set(tile.filename, tile);
    }
    const normalizedOrderedEntryCache = new Map();
    const resolvedTileIdsCache = new Map();
    function getNormalizedOrderedEntries(rawGroup) {
        const cached = normalizedOrderedEntryCache.get(rawGroup.id);
        if (cached) {
            return cached;
        }
        const normalized = normalizeOrderedEntriesForGroup(rawGroup, rawTileSetIndex, tileIndex);
        normalizedOrderedEntryCache.set(rawGroup.id, normalized);
        return normalized;
    }
    function getResolvedTileIdsForGroup(rawGroup, ancestry) {
        const cached = resolvedTileIdsCache.get(rawGroup.id);
        if (cached) {
            return cached;
        }
        const ownTileIds = rawGroup.tileIds.filter((tileId) => tileIndex.has(tileId));
        const orderedEntries = getNormalizedOrderedEntries(rawGroup);
        if (orderedEntries.length === 0) {
            resolvedTileIdsCache.set(rawGroup.id, ownTileIds);
            return ownTileIds;
        }
        const resolvedTileIds = [];
        for (const entry of orderedEntries) {
            if (entry.kind === "tile") {
                resolvedTileIds.push(entry.tileId);
                continue;
            }
            if (ancestry.has(entry.tileSetId)) {
                continue;
            }
            const childTileSet = rawTileSetIndex.get(entry.tileSetId);
            if (!childTileSet) {
                continue;
            }
            resolvedTileIds.push(...getResolvedTileIdsForGroup(childTileSet, new Set([...ancestry, entry.tileSetId])));
        }
        const uniqueResolvedTileIds = uniqueIds(resolvedTileIds);
        resolvedTileIdsCache.set(rawGroup.id, uniqueResolvedTileIds);
        return uniqueResolvedTileIds;
    }
    const groups = manifest.tileSets.map((rawGroup) => {
        const normalizedOrderedEntries = getNormalizedOrderedEntries(rawGroup);
        const tileIds = getResolvedTileIdsForGroup(rawGroup, new Set([rawGroup.id]));
        const filenames = tileIds
            .map((tileId) => tileIndex.get(tileId)?.filename)
            .filter((filename) => Boolean(filename));
        const customTileIds = rawGroup.customTileIds.filter((tileId) => tileIndex.has(tileId));
        const customFilenames = customTileIds
            .map((tileId) => tileIndex.get(tileId)?.filename)
            .filter((filename) => Boolean(filename));
        for (const tileId of customTileIds) {
            customTileIdSet.add(tileId);
        }
        for (const filename of customFilenames) {
            customFilenameSet.add(filename);
        }
        return {
            id: rawGroup.id,
            label: rawGroup.label,
            paintMode: normalizePaintMode(rawGroup),
            tileIds,
            orderedEntries: normalizedOrderedEntries.length > 0 ? normalizedOrderedEntries : undefined,
            filenames,
            taxonomyNodeIds: getManifestTaxonomyNodeIds(rawGroup),
            isCustom: rawGroup.isCustom,
            customTileIds,
            customFilenames,
            enabled: rawGroup.enabled,
        };
    });
    return {
        manifest,
        groups,
        tileSetIndex: new Map(groups.map((group) => [group.id, group])),
        tileIndex,
        tileByFilenameIndex,
        customTileIdSet,
        customFilenameSet,
    };
}
function deriveCreatorFromSource(input) {
    const ownerKey = input.ownerKey ?? input.namespace;
    const label = input.ownerKey ?? input.namespace;
    return {
        ownerKey,
        label,
        tileSetIds: [],
    };
}
function getBrowseTaxonomyNode(nodeId) {
    ensureCatalogStateHydrated();
    return browseState.taxonomy.find((node) => node.id === nodeId);
}
function isOceanTaxonomyNode(nodeId) {
    const oceanGroup = catalogState.tileSetIndex.get(DEFAULT_OCEAN_TILE_SET_ID);
    if (!oceanGroup)
        return false;
    return oceanGroup.taxonomyNodeIds.includes(nodeId);
}
function addRuntimeTileSetToBrowse(currentBrowse, input) {
    const creators = currentBrowse.creators.map((creator) => ({
        ...creator,
        tileSetIds: [...creator.tileSetIds],
    }));
    const creatorSource = deriveCreatorFromSource(input);
    const creatorIndex = creators.findIndex((creator) => creator.ownerKey === creatorSource.ownerKey);
    if (creatorIndex === -1) {
        creators.push({
            ...creatorSource,
            tileSetIds: [input.tileSetId],
        });
    }
    else if (!creators[creatorIndex].tileSetIds.includes(input.tileSetId)) {
        creators[creatorIndex].tileSetIds.push(input.tileSetId);
    }
    const taxonomy = currentBrowse.taxonomy.map((node) => ({
        ...node,
        tileSetEntries: input.taxonomyNodeIds.includes(node.id) &&
            !node.tileSetEntries.some((entry) => entry.tileSetId === input.tileSetId)
            ? [
                ...node.tileSetEntries,
                {
                    tileSetId: input.tileSetId,
                    sortOrder: node.tileSetEntries.reduce((highest, entry) => Math.max(highest, entry.sortOrder), -1) + 1,
                },
            ]
            : [...node.tileSetEntries],
        tileSetIds: input.taxonomyNodeIds.includes(node.id) && !node.tileSetIds.includes(input.tileSetId)
            ? [...node.tileSetIds, input.tileSetId]
            : [...node.tileSetIds],
    }));
    const rootTileSetEntries = input.taxonomyNodeIds.length === 0 &&
        !(currentBrowse.rootTileSetEntries ?? []).some((entry) => entry.tileSetId === input.tileSetId)
        ? [
            ...(currentBrowse.rootTileSetEntries ?? []),
            {
                tileSetId: input.tileSetId,
                sortOrder: (currentBrowse.rootTileSetEntries ?? []).reduce((highest, entry) => Math.max(highest, entry.sortOrder), -1) + 1,
            },
        ]
        : [...(currentBrowse.rootTileSetEntries ?? [])];
    return {
        ...currentBrowse,
        creators,
        taxonomy,
        rootTileSetEntries,
    };
}
function removeRuntimeTileSetFromBrowse(currentBrowse, tileSetId) {
    return {
        ...currentBrowse,
        creators: currentBrowse.creators
            .map((creator) => ({
            ...creator,
            tileSetIds: creator.tileSetIds.filter((id) => id !== tileSetId),
        }))
            .filter((creator) => creator.tileSetIds.length > 0),
        collections: currentBrowse.collections.map((collection) => ({
            ...collection,
            tileSetIds: collection.tileSetIds.filter((id) => id !== tileSetId),
        })),
        taxonomy: currentBrowse.taxonomy.map((node) => ({
            ...node,
            tileSetEntries: node.tileSetEntries.filter((entry) => entry.tileSetId !== tileSetId),
            tileSetIds: node.tileSetIds.filter((id) => id !== tileSetId),
        })),
        rootTileSetEntries: (currentBrowse.rootTileSetEntries ?? []).filter((entry) => entry.tileSetId !== tileSetId),
        tags: currentBrowse.tags.map((tag) => ({
            ...tag,
            tileSetIds: tag.tileSetIds.filter((id) => id !== tileSetId),
        })),
    };
}
/**
 * Mirrors the `paint-set_<uuid>` deterministic id pattern emitted by the
 * tiles+overlays unification migration so the engine can resolve a
 * decoration's singleton paint set even when the browse payload omits the
 * link (e.g., legacy fixtures and snapshots predating the unification).
 *
 * Source of truth: `supabase/migrations/20260424100000_unify_tiles_overlays.sql`
 * (`'paint-set_' || substring(o.id from position('_' in o.id) + 1)`).
 */
function deriveSingletonPaintSetIdFromDecorationId(decorationId) {
    const underscoreIndex = decorationId.indexOf("_");
    if (underscoreIndex < 0) {
        return `paint-set_${decorationId}`;
    }
    return `paint-set_${decorationId.slice(underscoreIndex + 1)}`;
}
function getDecorationBrushEntryKey(entry) {
    return entry.kind === "decoration"
        ? `decoration:${entry.decorationId}`
        : `paint-set:${entry.paintSetId}`;
}
function buildDecorationPaintSetIndices(decorations, declaredPaintSets) {
    const paintSetById = new Map();
    for (const paintSet of declaredPaintSets) {
        paintSetById.set(paintSet.id, paintSet);
    }
    const paintSetIdByDecoration = new Map();
    for (const decoration of decorations) {
        const declaredPaintSetId = decoration.paintSetId;
        const paintSetId = declaredPaintSetId ?? deriveSingletonPaintSetIdFromDecorationId(decoration.id);
        paintSetIdByDecoration.set(decoration.id, paintSetId);
        if (!paintSetById.has(paintSetId)) {
            paintSetById.set(paintSetId, {
                id: paintSetId,
                label: decoration.title,
                paintMode: "ordered",
                decorationIds: [decoration.id],
                orderedEntries: [{ kind: "decoration", decorationId: decoration.id }],
            });
        }
    }
    return {
        decorationPaintSetIndex: paintSetById,
        paintSetIdByDecorationId: paintSetIdByDecoration,
    };
}
let catalogState = buildCatalogState(EMPTY_MANIFEST);
let browseState = EMPTY_BROWSE;
let decorationIndex = new Map();
let decorationPaintSetIndex = new Map();
let paintSetIdByDecorationId = new Map();
export let DEFAULT_OCEAN_TILE_SET_ID = EMPTY_MANIFEST.defaults.defaultOceanTileSetId;
export let DEFAULT_SECONDARY_PAINT_TILE_SET_ID = EMPTY_MANIFEST.defaults.defaultSecondaryPaintTileSetId;
export let TILE_CATALOG = catalogState.groups;
function normalizeBrowseSnapshot(input) {
    return {
        creators: [...(input?.creators ?? [])],
        collections: [...(input?.collections ?? [])],
        taxonomy: [...(input?.taxonomy ?? [])],
        rootTileSetEntries: [...(input?.rootTileSetEntries ?? [])],
        decorations: [...(input?.decorations ?? [])],
        decorationPaintSets: input?.decorationPaintSets
            ? [...input.decorationPaintSets]
            : undefined,
        tags: [...(input?.tags ?? [])],
    };
}
function hasCatalogSnapshot(input) {
    return (input.manifest.tileSets.length > 0 ||
        input.manifest.tiles.length > 0 ||
        input.browse.creators.length > 0 ||
        input.browse.collections.length > 0 ||
        input.browse.taxonomy.length > 0 ||
        (input.browse.rootTileSetEntries?.length ?? 0) > 0 ||
        input.browse.decorations.length > 0 ||
        input.browse.tags.length > 0);
}
function syncCatalogState(nextState, options) {
    const nextBrowse = normalizeBrowseSnapshot(options?.browse);
    catalogState = nextState;
    browseState = nextBrowse;
    decorationIndex = new Map((browseState.decorations ?? []).map((decoration) => [
        decoration.id,
        {
            ...decoration,
            url: resolveTileAssetUrl(decoration.relativePath),
        },
    ]));
    ({ decorationPaintSetIndex, paintSetIdByDecorationId } = buildDecorationPaintSetIndices(browseState.decorations ?? [], browseState.decorationPaintSets ?? []));
    TILE_CATALOG = nextState.groups;
    DEFAULT_OCEAN_TILE_SET_ID = nextState.manifest.defaults.defaultOceanTileSetId;
    DEFAULT_SECONDARY_PAINT_TILE_SET_ID =
        nextState.manifest.defaults.defaultSecondaryPaintTileSetId;
    if (options?.syncStore ?? true) {
        useTileCatalogStore.setState((state) => ({
            status: "ready",
            error: null,
            revision: state.revision + 1,
            manifest: nextState.manifest,
            browse: nextBrowse,
        }));
    }
    else {
        useTileCatalogStore.setState({
            manifest: nextState.manifest,
            browse: nextBrowse,
        });
    }
}
let manifestLoadPromise = null;
let cachedPayload = null;
async function fetchTileCatalogPayload(fetchImpl) {
    if (cachedPayload)
        return cachedPayload;
    const response = await fetchImpl("/api/tile-catalog", { cache: "no-store" });
    if (!response.ok) {
        throw new Error(`Failed to fetch tile catalog: ${response.status}`);
    }
    const payload = (await response.json());
    cachedPayload = payload;
    return payload;
}
export const useTileCatalogStore = create((set) => ({
    status: "idle",
    error: null,
    revision: 0,
    manifest: EMPTY_MANIFEST,
    browse: EMPTY_BROWSE,
    async ensureLoaded() {
        if (manifestLoadPromise) {
            return manifestLoadPromise;
        }
        set({ status: "loading", error: null });
        manifestLoadPromise = loadTileCatalog()
            .then(() => {
            set((state) => ({
                status: "ready",
                error: null,
                revision: state.revision + 1,
            }));
        })
            .catch((error) => {
            set({
                status: "error",
                error: error instanceof Error ? error.message : "Failed to load tile catalog",
            });
            throw error;
        })
            .finally(() => {
            manifestLoadPromise = null;
        });
        return manifestLoadPromise;
    },
}));
function ensureCatalogStateHydrated() {
    const storeState = useTileCatalogStore.getState();
    if (!hasCatalogSnapshot({ manifest: storeState.manifest, browse: storeState.browse })) {
        return;
    }
    const moduleMissingCatalog = !hasCatalogSnapshot({
        manifest: catalogState.manifest,
        browse: browseState,
    });
    const missingManifestEntries = catalogState.manifest.tileSets.length === 0 && storeState.manifest.tileSets.length > 0;
    const missingBrowseEntries = browseState.decorations.length === 0 && storeState.browse.decorations.length > 0;
    if (moduleMissingCatalog || missingManifestEntries || missingBrowseEntries) {
        syncCatalogState(buildCatalogState(storeState.manifest), {
            browse: storeState.browse,
            syncStore: false,
        });
    }
}
export async function loadTileCatalog(fetchImpl = fetch) {
    const resolvedPayload = await fetchTileCatalogPayload(fetchImpl);
    syncCatalogState(buildCatalogState(resolvedPayload.manifest), {
        browse: resolvedPayload.browse,
        syncStore: false,
    });
}
export function loadTileCatalogFromPayload(payload) {
    cachedPayload = payload;
    syncCatalogState(buildCatalogState(payload.manifest), {
        browse: payload.browse,
        syncStore: false,
    });
}
export function resetTileCatalogForTests(manifest = EMPTY_MANIFEST) {
    cachedPayload = null;
    syncCatalogState(buildCatalogState(manifest), {
        browse: EMPTY_BROWSE,
        syncStore: false,
    });
    useTileCatalogStore.setState({
        status: "idle",
        error: null,
        revision: 0,
        manifest,
        browse: EMPTY_BROWSE,
    });
}
export function getTileCatalogBrowse() {
    ensureCatalogStateHydrated();
    return browseState;
}
export function getTileCatalogPayloadSnapshot() {
    ensureCatalogStateHydrated();
    return {
        manifest: catalogState.manifest,
        browse: browseState,
    };
}
export function getCatalogDecorationById(decorationId) {
    ensureCatalogStateHydrated();
    return decorationIndex.get(decorationId);
}
export function getCatalogDecorationUrl(decorationId) {
    return getCatalogDecorationById(decorationId)?.url;
}
export function getCatalogDecorationsForTaxonomyNode(taxonomyNodeId) {
    const node = getBrowseTaxonomyNode(taxonomyNodeId);
    if (!node) {
        return [];
    }
    return node.decorationIds
        .map((decorationId) => getCatalogDecorationById(decorationId))
        .filter((decoration) => Boolean(decoration));
}
// ---------------------------------------------------------------------------
// Decoration paint set helpers (mirror tile set helpers)
// ---------------------------------------------------------------------------
export function getDecorationPaintSetById(paintSetId) {
    ensureCatalogStateHydrated();
    return decorationPaintSetIndex.get(paintSetId);
}
export function getPaintSetIdForDecoration(decorationId) {
    ensureCatalogStateHydrated();
    return paintSetIdByDecorationId.get(decorationId);
}
export function getDecorationPaintSetForDecoration(decorationId) {
    const paintSetId = getPaintSetIdForDecoration(decorationId);
    return paintSetId ? getDecorationPaintSetById(paintSetId) : undefined;
}
function getDecorationBrushEntryCount(set) {
    return set.orderedEntries.length > 0 ? set.orderedEntries.length : set.decorationIds.length;
}
export function getDecorationPaintSetBehavior(set) {
    if (getDecorationBrushEntryCount(set) <= 1 && set.decorationIds.length <= 1) {
        return "single";
    }
    return set.paintMode;
}
export function isSingleDecorationPaintSet(set) {
    return getDecorationPaintSetBehavior(set) === "single";
}
export function isOrderedDecorationPaintSet(set) {
    return getDecorationBrushEntryCount(set) > 1 && getDecorationPaintSetBehavior(set) === "ordered";
}
export function isDynamicDecorationPaintSet(set) {
    return set.decorationIds.length > 1 && getDecorationPaintSetBehavior(set) === "dynamic";
}
export function getOrderedDecorationBrushEntries(set) {
    if (set.orderedEntries.length > 0) {
        return [...set.orderedEntries];
    }
    return set.decorationIds.map((decorationId) => ({ kind: "decoration", decorationId }));
}
export function getDefaultDecorationBrushEntry(set) {
    return getOrderedDecorationBrushEntries(set)[0] ?? null;
}
export function getCycledDecorationBrushEntry(set, currentEntry, step) {
    const entries = getOrderedDecorationBrushEntries(set);
    if (entries.length === 0) {
        return null;
    }
    const currentIndex = currentEntry
        ? entries.findIndex((entry) => getDecorationBrushEntryKey(entry) === getDecorationBrushEntryKey(currentEntry))
        : -1;
    const baseIndex = currentIndex === -1 ? 0 : currentIndex;
    const normalizedStep = ((step % entries.length) + entries.length) % entries.length;
    const nextIndex = (baseIndex + normalizedStep) % entries.length;
    return entries[nextIndex] ?? null;
}
export function getRandomDecorationIdFromPaintSet(set) {
    if (set.decorationIds.length === 0) {
        return null;
    }
    return set.decorationIds[Math.floor(Math.random() * set.decorationIds.length)] ?? null;
}
/**
 * Resolves a brush entry to a single overlay id, suitable for placement.
 * - `decoration`: returns the entry's decoration id directly.
 * - `paint-set`: evaluates the nested set per call, returning a random
 *   decoration id (mirrors how dynamic tile sets resolve per stroke).
 */
export function resolveDecorationBrushEntryToDecorationId(entry) {
    if (!entry) {
        return null;
    }
    if (entry.kind === "decoration") {
        return entry.decorationId;
    }
    const set = getDecorationPaintSetById(entry.paintSetId);
    return set ? getRandomDecorationIdFromPaintSet(set) : null;
}
export function getDecorationBrushEntryPreviewDecorationId(entry) {
    if (!entry) {
        return null;
    }
    if (entry.kind === "decoration") {
        return entry.decorationId;
    }
    const set = getDecorationPaintSetById(entry.paintSetId);
    return set ? set.decorationIds[0] ?? null : null;
}
export function getOfficialRootTaxonomyNodes() {
    ensureCatalogStateHydrated();
    return browseState.taxonomy.filter((node) => node.parentId === null &&
        (node.source ? node.source === "system" : node.isOfficial) &&
        node.tileSetIds.length > 0);
}
export function getTaxonomyNodeById(nodeId) {
    return getBrowseTaxonomyNode(nodeId);
}
export function isOfficialRootTaxonomyNode(nodeId) {
    const node = getBrowseTaxonomyNode(nodeId);
    if (!node || node.parentId !== null) {
        return false;
    }
    return node.source ? node.source === "system" : node.isOfficial;
}
export function getTileSetsForTaxonomyNode(nodeId) {
    const node = getBrowseTaxonomyNode(nodeId);
    if (node) {
        return node.tileSetIds
            .map((tileSetId) => getTileSetById(tileSetId))
            .filter((tileSet) => Boolean(tileSet));
    }
    return TILE_CATALOG.filter((group) => group.taxonomyNodeIds.includes(nodeId));
}
export function getDirectSelectTileSetForTaxonomyNode(nodeId) {
    if (!isOceanTaxonomyNode(nodeId)) {
        return null;
    }
    const group = getTileSetById(DEFAULT_OCEAN_TILE_SET_ID);
    return group && isTileSetEnabled(group) ? group : null;
}
export function getTileSetById(id) {
    ensureCatalogStateHydrated();
    return catalogState.tileSetIndex.get(id);
}
export function isTileSetEnabled(group) {
    return group.enabled !== false;
}
export function isCustomTileSet(group) {
    return group.isCustom === true;
}
export function isOceanTileSet(group) {
    if (group.id === DEFAULT_OCEAN_TILE_SET_ID)
        return true;
    return group.taxonomyNodeIds.some((nodeId) => isOceanTaxonomyNode(nodeId));
}
export function getCustomTileIdsForTileSet(group) {
    if (group.customTileIds && group.customTileIds.length > 0) {
        return group.customTileIds;
    }
    return group.isCustom ? group.tileIds : [];
}
export function getCustomFilenamesForTileSet(group) {
    if (group.customFilenames && group.customFilenames.length > 0) {
        return group.customFilenames;
    }
    return group.isCustom ? group.filenames : [];
}
export function isCustomTileIdInTileSet(group, tileId) {
    return getCustomTileIdsForTileSet(group).includes(tileId);
}
export function isCustomTileFilenameInTileSet(group, filename) {
    return getCustomFilenamesForTileSet(group).includes(filename);
}
export function isCustomTileId(tileId) {
    ensureCatalogStateHydrated();
    return catalogState.customTileIdSet.has(tileId);
}
export function isCustomTileFilename(filename) {
    ensureCatalogStateHydrated();
    return catalogState.customFilenameSet.has(filename);
}
export function getTileById(tileId) {
    ensureCatalogStateHydrated();
    return catalogState.tileIndex.get(tileId);
}
export function getTileByFilename(filename) {
    ensureCatalogStateHydrated();
    return catalogState.tileByFilenameIndex.get(filename);
}
export function getTileUrl(tileId) {
    return getTileById(tileId)?.url;
}
export function getTileUrlByFilename(filename) {
    return getTileByFilename(filename)?.url;
}
export function getRandomTileIdFromTileSet(group) {
    return group.tileIds[Math.floor(Math.random() * group.tileIds.length)];
}
export function getRandomFilenameFromTileSet(group) {
    return group.filenames[Math.floor(Math.random() * group.filenames.length)];
}
function getBrushEntryCount(group) {
    return group.orderedEntries && group.orderedEntries.length > 0
        ? group.orderedEntries.length
        : group.tileIds.length;
}
export function getTileSetBehavior(group) {
    if (group.tileIds.length === 1) {
        return "single";
    }
    return group.paintMode ?? (group.tileIds.length > 1 ? "dynamic" : "ordered");
}
export function isSingleLookTileSet(group) {
    return group.tileIds.length === 1;
}
export function isDynamicTileSet(group) {
    return group.tileIds.length > 1 && getTileSetBehavior(group) === "dynamic";
}
export function isOrderedTileSet(group) {
    return group.tileIds.length > 1 && getBrushEntryCount(group) > 1 && getTileSetBehavior(group) === "ordered";
}
export function getOrderedBrushEntries(group) {
    if (group.orderedEntries && group.orderedEntries.length > 0) {
        return [...group.orderedEntries];
    }
    return group.tileIds.map((tileId) => ({ kind: "tile", tileId }));
}
export function getDefaultBrushEntry(group) {
    return getOrderedBrushEntries(group)[0] ?? null;
}
export function getBrushEntryPreviewTileId(entry) {
    if (entry.kind === "tile") {
        return entry.tileId;
    }
    const tileSet = getTileSetById(entry.tileSetId);
    return tileSet ? getDefaultTileIdFromTileSet(tileSet) : null;
}
export function getCycledBrushEntry(group, currentEntry, step) {
    const entries = getOrderedBrushEntries(group);
    if (entries.length === 0) {
        return null;
    }
    const currentIndex = currentEntry
        ? entries.findIndex((entry) => getOrderedBrushEntryKey(entry) === getOrderedBrushEntryKey(currentEntry))
        : -1;
    const baseIndex = currentIndex === -1 ? 0 : currentIndex;
    const normalizedStep = ((step % entries.length) + entries.length) % entries.length;
    const nextIndex = (baseIndex + normalizedStep) % entries.length;
    return entries[nextIndex] ?? null;
}
export function resolveBrushEntryToPaintSelection(entry, parentTileSetId) {
    if (!entry) {
        return null;
    }
    if (entry.kind === "tile") {
        const tile = getTileById(entry.tileId);
        if (!tile) {
            return null;
        }
        const tileSetId = parentTileSetId ?? tile.tileSetId;
        return tileSetId ? { tileId: tile.id, tileSetId } : null;
    }
    const tileSet = getTileSetById(entry.tileSetId);
    if (!tileSet || !isDynamicTileSet(tileSet) || tileSet.tileIds.length === 0) {
        return null;
    }
    return {
        tileId: getRandomTileIdFromTileSet(tileSet),
        tileSetId: tileSet.id,
    };
}
export function getDefaultTileIdFromTileSet(group) {
    const entry = getDefaultBrushEntry(group);
    if (!entry) {
        return null;
    }
    return getBrushEntryPreviewTileId(entry);
}
export function getCycledTileIdFromTileSet(group, currentTileId, step) {
    const nextEntry = getCycledBrushEntry(group, currentTileId ? { kind: "tile", tileId: currentTileId } : null, step);
    return nextEntry ? getBrushEntryPreviewTileId(nextEntry) : null;
}
export function getAllTileIds() {
    const all = new Set();
    for (const group of TILE_CATALOG) {
        for (const tileId of group.tileIds) {
            all.add(tileId);
        }
    }
    return Array.from(all);
}
export function getAllTileFilenames() {
    const all = new Set();
    for (const group of TILE_CATALOG) {
        for (const filename of group.filenames) {
            all.add(filename);
        }
    }
    return Array.from(all);
}
export function resolveTileId(input) {
    if (input.tileId && getTileById(input.tileId)) {
        return input.tileId;
    }
    if (input.tileFilename) {
        const tile = getTileByFilename(input.tileFilename);
        if (tile) {
            return tile.id;
        }
    }
    if (input.tileSetId) {
        const group = getTileSetById(input.tileSetId);
        if (group) {
            return group.tileIds[0] ?? null;
        }
    }
    return null;
}
export function resolveTileSetId(input) {
    if (input.tileId) {
        const tile = getTileById(input.tileId);
        if (tile?.tileSetId && getTileSetById(tile.tileSetId)) {
            return tile.tileSetId;
        }
    }
    if (input.tileFilename) {
        const tile = getTileByFilename(input.tileFilename);
        if (tile?.tileSetId && getTileSetById(tile.tileSetId)) {
            return tile.tileSetId;
        }
    }
    if (input.tileSetId) {
        const group = getTileSetById(input.tileSetId);
        if (group) {
            return group.id;
        }
    }
    return null;
}
export function registerCustomTileAtRuntime(opts) {
    const targetTileSetId = opts.existingTileSetId ?? opts.tileSetId;
    const namespace = opts.namespace ?? "user";
    const ownerKey = opts.ownerKey ?? "local";
    const tileId = opts.tileId ?? createTileId();
    const relativePath = opts.relativePath ?? createCatalogAssetStoragePath(opts.assetId ?? tileId, opts.filename);
    if (getTileById(tileId)) {
        return tileId;
    }
    const manifest = structuredClone(catalogState.manifest);
    manifest.tiles.push({
        id: tileId,
        tileSetId: targetTileSetId,
        filename: opts.filename,
        namespace,
        relativePath,
    });
    let nextBrowse = browseState;
    const existingTileSet = manifest.tileSets.find((group) => group.id === targetTileSetId);
    if (existingTileSet) {
        if (!existingTileSet.tileIds.includes(tileId)) {
            existingTileSet.tileIds.push(tileId);
        }
        if (!existingTileSet.customTileIds.includes(tileId)) {
            existingTileSet.customTileIds.push(tileId);
        }
        if (Array.isArray(existingTileSet.orderedEntries) && existingTileSet.orderedEntries.length > 0) {
            existingTileSet.orderedEntries.push({ kind: "tile", tileId });
        }
        existingTileSet.isCustom = true;
    }
    else {
        manifest.tileSets.push({
            id: opts.tileSetId,
            label: opts.tileSetLabel,
            enabled: true,
            isCustom: true,
            paintMode: "ordered",
            tileIds: [tileId],
            customTileIds: [tileId],
            taxonomyNodeIds: uniqueIds(opts.taxonomyNodeIds),
        });
        nextBrowse = addRuntimeTileSetToBrowse(nextBrowse, {
            tileSetId: opts.tileSetId,
            namespace,
            ownerKey,
            taxonomyNodeIds: uniqueIds(opts.taxonomyNodeIds),
        });
    }
    syncCatalogState(buildCatalogState(manifest), { browse: nextBrowse });
    return tileId;
}
export function removeCustomTileAtRuntime(tileSetId, tileId) {
    const manifest = structuredClone(catalogState.manifest);
    const group = manifest.tileSets.find((entry) => entry.id === tileSetId);
    const tile = manifest.tiles.find((entry) => entry.id === tileId);
    if (!group || !tile) {
        return;
    }
    group.tileIds = group.tileIds.filter((entry) => entry !== tileId);
    group.customTileIds = group.customTileIds.filter((entry) => entry !== tileId);
    if (Array.isArray(group.orderedEntries)) {
        group.orderedEntries = group.orderedEntries.filter((entry) => !(entry.kind === "tile" && entry.tileId === tileId));
    }
    manifest.tiles = manifest.tiles.filter((entry) => entry.id !== tileId);
    let nextBrowse = browseState;
    if (group.tileIds.length === 0) {
        manifest.tileSets = manifest.tileSets.filter((entry) => entry.id !== tileSetId);
        nextBrowse = removeRuntimeTileSetFromBrowse(nextBrowse, tileSetId);
    }
    else if (group.customTileIds.length === 0) {
        group.isCustom = false;
    }
    syncCatalogState(buildCatalogState(manifest), { browse: nextBrowse });
}
